import { Component, Input, OnInit, SimpleChanges, OnDestroy, EventEmitter, Output, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { InvoiceDeliveryConfiguration } from '../../model/invoice-delivery-configuration';
import { GetInvoiceDeliveryMethodIcon, InvoiceDeliveryMethodToString, InvoiceDeliveryMethod } from '../../model/const/enum';
import { Subscription, catchError, finalize, switchMap, timeout } from 'rxjs';
import { ContactService } from '../../../rent/service/data/contact.service';
import { IAutoCompleteService } from '../../../core/component/autocomplete-remote/autocomplete-service.interface';
import { Contact } from '../../../rent/model/contact';
import { PostalAddress } from '../../../common/model/postal-address';
import { AutoCompleteSelectionItem } from '../../../core/component/autocomplete-remote/autocomplete-selection-item';

@Component({
    selector: 'app-invoice-delivery-configuration',
    templateUrl: './invoice-delivery-configuration.html',
    styleUrls: ['./invoice-delivery-configuration.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InvoiceDeliveryConfigurationComponent implements OnInit, OnDestroy {
  constructor(
    private _controlModelFactory: FormControlExFactory,
    private _controlGroupFactory: FormBuilder,
    contactService: ContactService
  ) {
    this.title = 'PROFILE_SETTINGS_FORM.INVOICING';
    this.showRecipient = false;
    this.contactService = contactService;
    this.filterService = contactService;
  }

  @Output() formChange: EventEmitter<any> = new EventEmitter();
  // Accept AbstractControl for template binding; used internally as a FormGroup.
  private _group: FormGroup;
  @Input() set group(value: AbstractControl) {
    this._group = value as FormGroup;
  }
  get group(): FormGroup {
    return this._group;
  }
  @Input() helpText: string;
  @Input() model: InvoiceDeliveryConfiguration;
  @Input() title: string;
  @Input() showTitle = true;
  @Input() showRecipient: boolean;
  @Input() set contactFilterService(service: IAutoCompleteService) {
    this.filterService = service;
  }

  private _subscription: Subscription = new Subscription();
  filterService: IAutoCompleteService;
  contactService: ContactService;

  setEmailRequired() {
    let method = this.group.controls['deliveryMethod'].value;
    let isRequired = method === InvoiceDeliveryMethod.EmailWithoutPDF || method === InvoiceDeliveryMethod.EmailWithPDF;

    this.group.controls['email'].validator = isRequired ? Validators.compose([Validators.email, Validators.required]) : Validators.email;
  }

  invoiceDeliveryMethods = [
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Mail), value: InvoiceDeliveryMethod.Mail, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Mail) },
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Manual), value: InvoiceDeliveryMethod.Manual, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Manual) },
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EmailWithPDF), value: InvoiceDeliveryMethod.EmailWithPDF, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EmailWithPDF) },
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EmailWithoutPDF), value: InvoiceDeliveryMethod.EmailWithoutPDF, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EmailWithoutPDF) },
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EInvoiceB2B), value: InvoiceDeliveryMethod.EInvoiceB2B, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EInvoiceB2B) },
  ];

  ngOnDestroy() {
    if (this._subscription) this._subscription.unsubscribe();
  }

  private ignoreChangeEvent: boolean = false;

  recipientChanged(recipientData: AutoCompleteSelectionItem<Contact>) {
    if (!recipientData || !recipientData.data) return;
    if (this.model.recipientContactId == recipientData.data.id) return;
    this.ignoreChangeEvent = true;
    this.contactService
      .get(recipientData.data.id)
      .pipe(
        catchError(e => {
          this.ignoreChangeEvent = false;
          throw e;
        })
      )
      .subscribe(recipient => {
        this.model.recipientContactId = recipient.id;
        this.model.postalAddress = recipient.invoiceAddress;
        let address = this.group.controls['postalAddress'] as FormGroup;
        address.controls['name'].setValue(recipient.name);
        address.controls['additionalName'].setValue(recipient.invoiceAddress.additionalName);
        address.controls['city'].setValue(recipient.invoiceAddress.city);
        address.controls['country'].setValue(recipient.invoiceAddress.country);
        address.controls['postalCode'].setValue(recipient.invoiceAddress.postalCode);
        address.controls['street'].setValue(recipient.invoiceAddress.street);
        this.group.controls['email'].setValue(recipient.emailInvoicing);
        this.ignoreChangeEvent = false;
        this.formChange.emit(this.group.value);
      });
  }

  ngOnInit() {
    let contactIdControl = this._controlModelFactory.createMandatory('LEASE_AGREEMENT.PAYER', this.model.recipientContactId);
    this.group.addControl('recipientContactId', contactIdControl);
    this.group.addControl('email', this._controlModelFactory.createEmail('COMMON.EMAIL', this.model.email));
    let methodControl = this._controlModelFactory.createMandatory('LEASE_AGREEMENT.INVOICE_DELIVERY_METHOD', this.model.deliveryMethod);
    this.group.addControl('deliveryMethod', methodControl);
    this.group.addControl('postalAddress', this._controlGroupFactory.group({}));
    this.group.addControl('id', this._controlModelFactory.createHidden(this.model.id));

    this._subscription.add(
      methodControl.valueChanges.subscribe(val => {
        this.setEmailRequired();
      })
    );
    this.setEmailRequired();
    this._subscription.add(
      this.group.valueChanges.subscribe(val => {
        if (!this.group.pristine && !this.ignoreChangeEvent) this.formChange.emit(val);
      })
    );
  }
}
